import { DEPRECATED_VIDEO_MODEL_REPLACEMENTS } from '@inference.engine/widgets/web';
import {
  VideoSettings,
  VideoModel,
  VIDEO_MODEL_CONFIG,
  VEO_ASPECT_RATIOS,
  RUNWAY_ASPECT_RATIOS,
  DURATIONS,
  OMNI_DURATIONS,
  OMNI_RESOLUTIONS,
  type VideoOptions,
  type Resolution,
  RESOLUTIONS_FOR_ASPECT,
  type VeoAspectRatio,
  type RunwayAspectRatio,
  type VideoFrame,
  VEO_REFERENCE_IMAGE_TYPES,
} from './VideoGenerationOptions.types';
import { FileStatus } from '@/features/chats/model/useFileAttachments/useFileAttachments.types';
import type { VeoVideoOptions } from './models/VeoOptions';
import type { RunwayVideoOptions } from './models/RunwayOptions';
import type { OmniVideoOptions } from './models/OmniOptions';

/**
 * Validates an override value against a list of valid values.
 * Returns the override if valid, otherwise returns the default value.
 */
function resolveValidValue<T extends string | number>(params: {
  overrideValue: string | number | undefined;
  validValues: readonly T[];
  defaultValue: T;
}): T {
  const { overrideValue, validValues, defaultValue } = params;
  if (
    overrideValue !== undefined &&
    (validValues as readonly (string | number)[]).includes(overrideValue)
  ) {
    return overrideValue as T;
  }
  return defaultValue;
}

const DEFAULT_ASPECT_RATIO: VeoAspectRatio = '16:9';
const DEFAULT_RESOLUTION: Resolution = '720p';
const DEFAULT_DURATION = 8;

/** The single duration veo renders whenever one of its constraints applies. */
export const VEO_FORCED_DURATION = 8;

/**
 * Returns the durations veo accepts for the given settings.
 * An end frame, 1080p and reference images each restrict generation to a single duration.
 */
export function getVeoAllowedDurations(params: {
  resolution?: Resolution;
  hasReferenceImages?: boolean;
  endFrame?: VideoFrame | null;
}) {
  const { resolution, hasReferenceImages, endFrame } = params;
  if (resolution === '1080p' || hasReferenceImages || endFrame) {
    return DURATIONS.filter((option) => option.value === VEO_FORCED_DURATION);
  }
  return DURATIONS;
}

export function isVeoModel(model: string | undefined): model is 'veo-3.1' | 'veo-3.1-fast' {
  return model === 'veo-3.1' || model === 'veo-3.1-fast';
}

export function hasVeoReferenceImages(files: readonly { status: string; mimeType: string }[]) {
  return files.some(
    (file) =>
      (file.status === FileStatus.UPLOADED || file.status === FileStatus.REFERENCED) &&
      VEO_REFERENCE_IMAGE_TYPES.includes(file.mimeType),
  );
}

/**
 * Whether attached images rule out start and end frames: Veo refuses a request carrying both, and
 * every image attached to a Veo turn is sent as a reference image.
 */
export function imagesBlockVeoFrames(model: string | undefined, hasReferenceImages: boolean) {
  return hasReferenceImages && isVeoModel(model);
}

export type VeoFrameSettings =
  | { model?: string; startFrame?: unknown; endFrame?: unknown }
  | undefined;

export function hasVeoFramesWithReferenceImages(
  settings: VeoFrameSettings,
  hasReferenceImages: boolean,
): boolean {
  return (
    imagesBlockVeoFrames(settings?.model, hasReferenceImages) &&
    (settings?.startFrame != null || settings?.endFrame != null)
  );
}

/**
 * Veo interpolates from a start frame and Omni binds a last frame only beside a first one, so the
 * API refuses an end frame sent without a start frame for both.
 */
export function hasEndFrameWithoutStartFrame(settings: VeoFrameSettings): boolean {
  const model = settings?.model;
  return (
    (isVeoModel(model) || model === 'gemini-omni-1.1-flash') &&
    settings?.endFrame != null &&
    settings.startFrame == null
  );
}

/**
 * Corrects veo settings to a combination the provider accepts, returning the same object when it is
 * already valid so callers can guard an update with an identity check.
 * Resolution is resolved before duration, because the duration rule reads the resolution.
 */
export function normaliseVeoSettings<T extends VeoVideoOptions>(
  settings: T,
  context: { hasReferenceImages?: boolean } = {},
): T {
  const allowedResolutions = RESOLUTIONS_FOR_ASPECT[settings.aspectRatio];
  const resolution = allowedResolutions.includes(settings.resolution)
    ? settings.resolution
    : allowedResolutions[0];

  const allowedDurations = getVeoAllowedDurations({
    resolution,
    hasReferenceImages: context.hasReferenceImages,
    endFrame: settings.endFrame,
  });
  const durationSeconds = allowedDurations.some(
    (option) => option.value === settings.durationSeconds,
  )
    ? settings.durationSeconds
    : VEO_FORCED_DURATION;

  if (resolution === settings.resolution && durationSeconds === settings.durationSeconds) {
    return settings;
  }

  return { ...settings, resolution, durationSeconds };
}

/**
 * Creates VideoOptions for a given model with optional overrides.
 * Applies model-specific conditional logic to determine which fields
 * should be set based on the model type, with per-property validation.
 * This is the single source of truth for model-dependent option creation —
 * shared between the V1 legacy popup and the V2 popup.
 */
/**
 * A chat keeps the model the user last picked, and `enabledModels` is server-derived, so a value
 * outside the union arrives whenever a stored chat outlives a model's removal or the backend
 * learns a model before the frontend does. Resolving it here beats throwing: the mount-time seed
 * in `ChatInputV2` runs inside an effect, where a throw unmounts the composer rather than
 * surfacing, and wrong-but-usable defaults leave the user a working picker.
 */
function resolveStoredModel(model: VideoModel): VideoModel {
  // A renamed id resolves to its current name rather than the default, which would move the user
  // from the Fast tier to Standard at four times the price.
  const renamed = DEPRECATED_VIDEO_MODEL_REPLACEMENTS.get(model);
  if (renamed && Object.hasOwn(VIDEO_MODEL_CONFIG, renamed)) {
    return renamed as VideoModel;
  }
  return Object.hasOwn(VIDEO_MODEL_CONFIG, model) ? model : 'veo-3.1';
}

export function getOptionsForModel(
  requestedModel: VideoModel,
  overrides?: Partial<VideoSettings>,
  context: { hasReferenceImages?: boolean } = {},
): VideoOptions {
  const model = resolveStoredModel(requestedModel);
  if (isVeoModel(model)) {
    const veoSettings = (overrides || {}) as Partial<
      VeoVideoOptions & { startFrame?: VideoFrame | null; endFrame?: VideoFrame | null }
    >;

    const validAspectRatios = VEO_ASPECT_RATIOS.map((ar) => ar.value);
    const aspectRatio = resolveValidValue({
      overrideValue: veoSettings.aspectRatio,
      validValues: validAspectRatios,
      defaultValue: DEFAULT_ASPECT_RATIO,
    });

    const validResolutions = RESOLUTIONS_FOR_ASPECT[aspectRatio];
    const resolution = resolveValidValue({
      overrideValue: veoSettings.resolution,
      validValues: validResolutions,
      defaultValue: DEFAULT_RESOLUTION,
    });

    const endFrame = veoSettings.endFrame ?? null;
    const durationSeconds = resolveValidValue({
      overrideValue: veoSettings.durationSeconds,
      validValues: getVeoAllowedDurations({
        resolution,
        endFrame,
        hasReferenceImages: context.hasReferenceImages,
      }).map((d) => d.value),
      defaultValue: DEFAULT_DURATION,
    });

    return {
      settings: {
        model,
        aspectRatio,
        resolution,
        durationSeconds,
        startFrame: veoSettings.startFrame ?? null,
        endFrame,
      },
    };
  }

  switch (model) {
    case 'gemini-omni-1.1-flash': {
      const omniSettings = (overrides || {}) as Partial<OmniVideoOptions>;
      return {
        settings: {
          model,
          aspectRatio: resolveValidValue({
            overrideValue: omniSettings.aspectRatio,
            validValues: VEO_ASPECT_RATIOS.map((ar) => ar.value),
            defaultValue: DEFAULT_ASPECT_RATIO,
          }),
          resolution: resolveValidValue({
            overrideValue: omniSettings.resolution,
            validValues: OMNI_RESOLUTIONS,
            defaultValue: DEFAULT_RESOLUTION,
          }),
          durationSeconds: resolveValidValue({
            overrideValue: omniSettings.durationSeconds,
            validValues: OMNI_DURATIONS.map((d) => d.value),
            defaultValue: DEFAULT_DURATION,
          }),
          startFrame: omniSettings.startFrame ?? null,
          endFrame: omniSettings.endFrame ?? null,
        },
      };
    }
    case 'runway-gen-4':
    case 'runway-gen-4.5': {
      const runwaySettings = (overrides || {}) as Partial<
        RunwayVideoOptions & { startFrame?: VideoFrame | null; endFrame?: VideoFrame | null }
      >;

      const DEFAULT_RUNWAY_ASPECT_RATIO: RunwayAspectRatio = '1280:720';
      const DEFAULT_RUNWAY_DURATION = 4;

      const validAspectRatios = RUNWAY_ASPECT_RATIOS.map((ar) => ar.value);
      const aspectRatio = resolveValidValue({
        overrideValue: runwaySettings.aspectRatio,
        validValues: validAspectRatios,
        defaultValue: DEFAULT_RUNWAY_ASPECT_RATIO,
      });

      const durationSeconds = resolveValidValue({
        overrideValue: runwaySettings.durationSeconds,
        validValues: DURATIONS.map((d) => d.value),
        defaultValue: DEFAULT_RUNWAY_DURATION,
      });

      return {
        settings: {
          model,
          aspectRatio,
          durationSeconds,
          startFrame: runwaySettings.startFrame ?? null,
          endFrame: runwaySettings.endFrame ?? null,
        },
      };
    }
    default:
      // `never`, so adding a VideoModel without a branch fails to compile.
      return assertNoVideoModelLeft(model);
  }
}

function assertNoVideoModelLeft(model: never): never {
  throw new Error(`Unsupported video model: ${String(model)}`);
}
